/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, message, Alert, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useQuery, useMutation, queryCache } from 'react-query';
import {
  getOfferFinalizationTemplate,
  getOfferReopenTemplate,
  updateOfferFinalizationTemplate,
  updateOfferReopenTemplate,
} from '~src/services/api/notifications';
import { OfferNotificationPreview } from './OfferNotificationPreview';
import { OfferNotificationRecipientsDrawer } from './OfferNotificationRecipientsDrawer';

interface OfferNotificationTabProps {
  type: 'finalize' | 'reopen';
  projectId: number;
  tenderId: number;
  tenderClosed: boolean;
  onUnsavedChangesChange?: (hasChanges: boolean, isInEditMode: boolean) => void;
}

export const OfferNotificationTab: React.FC<OfferNotificationTabProps> = ({
  type,
  projectId,
  tenderId,
  tenderClosed,
  onUnsavedChangesChange,
}) => {
  const intl = useIntl();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [recipientsDrawerVisible, setRecipientsDrawerVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');

  const isFinalize = type === 'finalize';
  const getTemplateFn = isFinalize
    ? getOfferFinalizationTemplate
    : getOfferReopenTemplate;
  const updateTemplateFn = isFinalize
    ? updateOfferFinalizationTemplate
    : updateOfferReopenTemplate;

  // Load template
  const { data: template, isLoading, error } = useQuery(
    ['offer-notification-template', type, projectId, tenderId],
    () => getTemplateFn({ projectId, tenderId }),
    {
      enabled: !!projectId && !!tenderId,
      retry: false, // Don't retry on 404
      refetchOnWindowFocus: false, // Don't refetch on window focus
      onSuccess: (data) => {
        setTitle(data.title);
        setDescription(data.description);
        setOriginalTitle(data.title);
        setOriginalDescription(data.description);
      },
      onError: (err: any) => {
        // Don't crash page on 404 - template might not exist yet
        console.warn('Template load failed:', err);
        // Set empty values so user can create new template
        setTitle('');
        setDescription('');
        setOriginalTitle('');
        setOriginalDescription('');
      },
    }
  );

  // Save mutation
  const [doUpdateTemplate, { isLoading: isSaving }] = useMutation(
    () => updateTemplateFn({ projectId, tenderId, title, description }),
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'notification-template-saved' })
        );
        setOriginalTitle(title);
        setOriginalDescription(description);
        setIsEditMode(false);
        queryCache.invalidateQueries([
          'offer-notification-template',
          type,
          projectId,
          tenderId,
        ]);
      },
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'notification-template-save-failed' })
        );
      },
    }
  );

  // Track changes
  const hasChanges = template
    ? title !== template.title || description !== template.description
    : title !== '' || description !== '';

  // Notify parent of unsaved changes
  useEffect(() => {
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(hasChanges, isEditMode);
    }
  }, [hasChanges, isEditMode, onUnsavedChangesChange]);

  const handleReset = () => {
    setTitle(originalTitle);
    setDescription(originalDescription);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    handleReset();
  };

  const handleSave = () => {
    if (!hasChanges) return;
    doUpdateTemplate();
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <div
        style={{
          display: 'flex',
          gap: 24,
          alignItems: 'stretch',
          minHeight: 520,
        }}>
        {/* Form Block - Left Side */}
        <div
          style={{
            width: 'calc(50% - 12px)',
            minWidth: '450px',
            background: '#fafafa',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            padding: 20,
          }}>
          <Alert
            message={intl.formatMessage({
              id: 'offer-notification-modal-info-title',
            })}
            description={intl.formatMessage({
              id: 'offer-notification-modal-info-description',
            })}
            type="info"
            showIcon
            style={{ marginBottom: 16, padding: '8px 12px' }}
          />

          <Typography.Title
            level={5}
            style={{ marginTop: 0, marginBottom: 16 }}>
            {intl.formatMessage({ id: 'notification-template-settings' })}
          </Typography.Title>

          <Form layout="vertical">
            <Form.Item
              label={intl.formatMessage({
                id: 'offer-notification-template-title-label',
              })}
              style={{ marginBottom: 16 }}>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'offer-notification-template-title-placeholder',
                })}
                disabled={!isEditMode || tenderClosed}
              />
            </Form.Item>

            <Form.Item
              label={intl.formatMessage({
                id: 'offer-notification-template-description-label',
              })}
              style={{ marginBottom: 16 }}>
              <Input.TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'offer-notification-template-description-placeholder',
                })}
                rows={3}
                disabled={!isEditMode || tenderClosed}
              />
            </Form.Item>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
              }}>
              {!isEditMode ? (
                <Button
                  type="primary"
                  onClick={handleEdit}
                  disabled={isLoading || tenderClosed}
                  icon={<EditOutlined rev={''} />}>
                  {intl.formatMessage({ id: 'edit' })}
                </Button>
              ) : (
                <React.Fragment>
                  <Button onClick={handleCancel}>
                    {intl.formatMessage({ id: 'cancel' })}
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={!hasChanges || tenderClosed}>
                    {intl.formatMessage({ id: 'save' })}
                  </Button>
                </React.Fragment>
              )}
            </div>
          </Form>
        </div>

        {/* Preview Block - Right Side */}
        <div
          style={{
            width: 'calc(50% - 12px)',
            display: 'flex',
          }}>
          <OfferNotificationPreview
            title={title}
            description={description}
            type={type}
            projectId={projectId}
            tenderId={tenderId}
          />
        </div>
      </div>

      <OfferNotificationRecipientsDrawer
        visible={recipientsDrawerVisible}
        onClose={() => setRecipientsDrawerVisible(false)}
        type={type}
        projectId={projectId}
        tenderId={tenderId}
      />
    </div>
  );
};
